import { sortBy, startCase } from 'lodash';
import { FilterContext } from 'api/automation-builder/types';
import { FieldSection, FieldTypeDisplay } from 'api/field-sections/types';
import type { Team, User as UserT } from 'api/models';
import { ProjectBudgetItemLite } from 'api/project';
import { SprintPoints } from 'api/sprint-planning/types';
import type {
  CustomFieldValueSourceType,
  Field,
  FieldOptionBase,
} from 'api/tasks';
import type { AssigneeValue as AssigneeValueType } from 'api/tasks/assignee-value';
import {
  FIELD_ID_ASSIGNEE,
  FieldType,
  filterableFieldType,
  NativeFieldName,
  SourceType,
  UserTypeFields,
} from 'api/tasks/field-meta';
import type { UsersMetadata } from 'api/tasks/users-metadata';
import { FieldTypes } from 'components/organisms/module-settings/fields/types';

export type {
  ProjectUsers,
  ProjectUsersByAccess,
  UsersMetadata,
} from 'api/tasks/users-metadata';

export {
  FIELD_ID_ASSIGNEE,
  FieldType,
  filterableFieldType,
  NativeFieldName,
  SourceType,
  UserTypeFields,
};

export interface FieldValueSourceMeta {
  fieldValueSourceFieldInfo?: Field;
  isMandatory?: boolean;
  fieldValueSourceType?: CustomFieldValueSourceType;
  fallbackFieldValue?: any;
}

export interface FieldBase<T> {
  fieldId: number;
  fieldName: string;
  fieldLabel?: string;
  fieldIndex?: number;
  description?: string;
  fieldGroup?: string;
  private: boolean;
  value?: T;
  defaultValue?: T;
  customField?: boolean;
  metaData?: {
    required?: boolean;
    relatedFieldId?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    options?: FieldOptionBase<number>[];
    roleIds?: number[];
    /**
     * This is just a FE property
     */
    allowRelativeDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowAbsDateOps?: boolean;
    /**
     * This is just a FE property
     */
    filterContext?: FilterContext;
    pointType?: SprintPoints;
  } & FieldValueSourceMeta;
  userCreated?: boolean;
  isEnabled?: boolean;
  isEditable?: boolean;
  fieldType?: string;
  label?: string;
  sourceType: SourceType;
  userGroupIds?: number[];
  objectType?: string; // TODO: fix circular dependency in typing
  objectSubType?: string;
  fieldTypeDisplay?: FieldTypeDisplay | null;
  metaFieldValue?: any;
  fieldValue?: any;
}

export interface BooleanField extends FieldBase<boolean> {
  type: FieldType.Boolean;
}

export interface NumberField extends FieldBase<number> {
  type: FieldType.Number;
  operation?: string;
  metaData?: {
    required: boolean;
    default?: number;
    range?: {
      min?: number;
      max?: number;
    };
    options?: FieldOptionBase<number>[];
    rangeAvailable: boolean;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
    roleIds?: number[];
  } & FieldValueSourceMeta;
  renderAs?: string;
}

export interface EstimateField extends FieldBase<number> {
  type: FieldType.Estimate;
  metaData?: {
    required: boolean;
    default?: number;
    options?: FieldOptionBase<number>[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
  } & FieldValueSourceMeta;
}

export interface TextField extends FieldBase<string> {
  type: FieldType.Text;
}

export interface MultiLineTextField extends FieldBase<string> {
  type: FieldType.MultiLineText;
}

export interface DateField<T extends number | string = string>
  extends FieldBase<T> {
  type: FieldType.Date;
  hideFutureDates?: boolean;
  metaData?: {
    required?: boolean;
    allowAdminsAndOwnersToEdit?: boolean;
    options?: FieldOptionBase<number>[];
    captureKeyIntervalForFirstActivityOnly?: boolean;
    /**
     * This is just a FE property
     */
    allowRelativeDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowAbsDateOps?: boolean;
    /**
     * This is just a FE property
     */
    allowBaseOps?: boolean;
    /**
     * This is just a FE property
     */
    showAllOps?: boolean;
    /**
     * This is just a FE property
     */
    filterContext?: FilterContext;
    pointType?: SprintPoints;
  } & FieldValueSourceMeta;
}

export interface RatingField<T extends number | string = string>
  extends FieldBase<T> {
  type: FieldType.Number;
  fieldType: FieldTypes.Rating;
  hideFutureDates?: boolean;
  metaData?: {
    required?: boolean;
    ratingScale?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
  } & FieldValueSourceMeta;
}

export interface UserFieldMetadata extends FieldValueSourceMeta {
  required?: boolean;
  users: UsersMetadata;
  options?: FieldOptionBase<number>[];
  allowAdminsAndOwnersToEdit?: boolean;
  captureKeyIntervalForFirstActivityOnly?: boolean;
  pointType?: SprintPoints;
}

export interface SingleUserField extends FieldBase<number> {
  type: FieldType.SingleUser;
  metaData: UserFieldMetadata;
}

export interface MultiUserField extends FieldBase<number[]> {
  type: FieldType.MultiUser;
  metaData: UserFieldMetadata;
}

export interface SingleSelectField extends FieldBase<number> {
  type: FieldType.SingleSelect;
  metaData: {
    required?: boolean;
    options: FieldOptionBase[];
    default?: number;
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
    roleIds?: number[];
  } & FieldValueSourceMeta;
}

export interface MultiSelectField extends FieldBase<number[]> {
  type: FieldType.MultiSelect;
  metaData: {
    required?: boolean;
    options: FieldOptionBase[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
    roleIds?: number[];
  } & FieldValueSourceMeta;
}

export interface BudgetField extends FieldBase<ProjectBudgetItemLite[]> {
  type: FieldType.MultiSelect;
  fieldDataType?: string;
  fieldType?: string;
  optionsAvailable?: boolean;
  metaData: {
    required?: boolean;
    options: FieldOptionBase[];
  };
}

export interface NoteField extends FieldBase<string> {
  type: FieldType.Note;
  metaData: {
    required?: boolean;
    tags: FieldOptionBase[];
    options?: FieldOptionBase[];
    allowAdminsAndOwnersToEdit?: boolean;
    captureKeyIntervalForFirstActivityOnly?: boolean;
    pointType?: SprintPoints;
  } & FieldValueSourceMeta;
}

export type UpdatableSquad = {
  users: Pick<UserT, 'userId'>[];
  teams: Pick<Team, 'teamId'>[];
};

export type UserId = ['user', number, UserT | undefined];
export const UserId = (id: number, user?: UserT): UserId =>
  (user ? ['user', id, user] : ['user', id]) as UserId;

export type TeamId = ['team', number];
export const TeamId = (id: number): TeamId => ['team', id];

export type AssigneeValue = AssigneeValueType;

export const AssigneeValue = {
  toString: (value: AssigneeValueType): string => value.join('_'),
  fromString: (value: string): AssigneeValueType => {
    const [type, id] = value.split('_');

    return [type, parseInt(id, 10)] as AssigneeValueType;
  },
  isSame: (a: AssigneeValueType, b: AssigneeValueType): boolean =>
    a[0] === b[0] && a[1] === b[1],
  toSquad: (values: AssigneeValueType[]): UpdatableSquad => {
    return {
      users: values
        .filter(([type]) => type === 'user')
        .map(([, userId]) => ({ userId })),
      teams: values
        .filter(([type]) => type === 'team')
        .map(([, teamId]) => ({ teamId })),
    };
  },
};

export interface AssigneeField extends FieldBase<AssigneeValue[]> {
  type: FieldType.Assignee;
  fieldGroup: string;
  metaData: UserFieldMetadata & {
    teams: Team[];
    projectPlaceholders?: { [id: number]: Team[] };
  };
}

export const AssigneeField = (
  users: UsersMetadata,
  teams: Team[],
): AssigneeField => ({
  fieldId: FIELD_ID_ASSIGNEE,
  private: false,
  type: FieldType.Assignee,
  customField: false,
  fieldName: 'assignee',
  metaData: { users, teams },
  fieldGroup: '_native',
  sourceType: SourceType.Task,
});

export type FilterableField =
  | BooleanField
  | NumberField
  | TextField
  | MultiLineTextField
  | DateField
  | SingleUserField
  | MultiUserField
  | SingleSelectField
  | MultiSelectField
  | AssigneeField
  | EstimateField
  | NoteField;

export type FieldLookupByName = Record<string, FilterableField>;
export type FieldLookupById = Record<number, FilterableField>;

export interface GroupedOptions<T> {
  id: string;
  label?: string;
  icon?: React.JSX.Element;
  fieldSections?: FieldSection[] | undefined;
  options: T[];
}
export type LabelledOptions<T> = Omit<
  GroupedOptions<T>,
  'id' | 'icon' | 'fieldSections'
> & {
  label?: React.JSX.Element | string;
};

export interface GroupedOptionsBySection<T, R>
  extends Omit<GroupedOptions<T>, 'options'> {
  options: R[];
}

export const groupedFieldsTransformerFunction = (
  fieldOption: GroupedOptions<FilterableField>,
): GroupedOptions<FilterableField> => ({
  ...fieldOption,
  label: fieldOption.label || startCase(fieldOption.id),
  options: sortBy(
    fieldOption.options.map((_option) => ({
      label: _option.fieldLabel,
      value: _option.fieldId as any,
      ..._option,
    })),
    ({ fieldLabel, fieldName }) => fieldLabel ?? startCase(fieldName),
  ),
});
